import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosNoAuth } from "../config/axiosInstance";
import { NINJACURATE_BACKEND_BASE_URL } from "../config/api_config";

// Async thunk to get user old UUID
export const getUserOlduuid = createAsyncThunk(
  "postPurchase/getUserOlduuid",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axiosNoAuth.post(`${NINJACURATE_BACKEND_BASE_URL}/user_authenticate`, {
        user_email: email,
      });
      return response?.data;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const registerCoreProductPurchase = createAsyncThunk(
  "postPurchase/registerCoreProductPurchase",
  async ({ name, email, phone_number, razorpay_order_id, razorpay_payment_id }, { rejectWithValue }) => {
    try {
      const response = await axiosNoAuth.post(`https://xgfy-czuw-092q.m2.xano.io/api:beta_certified/utility/fallback/core_product_purchase`,{
        name: name,
        email: email,
        phone_number: phone_number,
        razorpay_order_id: razorpay_order_id,
        razorpay_payment_id: razorpay_payment_id,
        product_id : 7
      });
      return response?.data;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for long polling course unlock status
export const longPollCourseUnlocked = createAsyncThunk(
  "postPurchase/longPollCourseUnlocked",
  async ({ user_id, courseNames, maxAttempts = 30, interval = 2000 }, { dispatch, signal, rejectWithValue }) => {
    let attempts = 0;
    
    const poll = async () => {
      try {
        // Check if request was cancelled
        if (signal?.aborted) {
          throw new Error("Polling cancelled");
        }

        const response = await axiosNoAuth.post(
          `${NINJACURATE_BACKEND_BASE_URL}/check_if_course_unlocked`,
          {
            user_id: user_id,
            courses: courseNames,
          }
        );
        
        const resData = response?.data;

        if (resData?.is_course_unlocked === true) {
          return { success: true, data: resData };
        }
        else {
          dispatch(setLongPollingStatus({is_course_unlocked: false}));
        }

        attempts++;
        if (attempts >= maxAttempts) {
          return { success: false, message: "Max polling attempts reached" };
        }

        await new Promise(resolve => setTimeout(resolve, interval));
        return poll();

      } catch (error) {
        console.error("Polling error:", error.message);
        
        // If cancelled, don't retry
        if (signal?.aborted || error.message === "Polling cancelled") {
          throw error;
        }
        
        attempts++;
        if (attempts >= maxAttempts) {
          return rejectWithValue("Max polling attempts reached");
        }

        await new Promise(resolve => setTimeout(resolve, interval));
        return poll();
      }
    };

    return poll();
  }
);

const postPurchaseSlice = createSlice({
  name: 'postPurchase',
  initialState: {
    // User authentication state
    oldUuid: null,
    getUserOlduuidLoading: false,
    getUserOlduuidError: null,
    
    // Long polling state
    longPollingStatus: null,
    longPollingLoading: false,
    longPollingError: null,
    
    // Course unlock status
    courseUnlockData: null,
    isPollingActive: false,
  },
  reducers: {
    setLongPollingStatus: (state, action) => {
      state.longPollingStatus = action.payload;
    },
    resetPostPurchaseState: () => {
      return {
        // User authentication state
        oldUuid: null,
        getUserOlduuidLoading: false,
        getUserOlduuidError: null,
        
        // Long polling state
        longPollingStatus: null,
        longPollingLoading: false,
        longPollingError: null,
        
        // Course unlock status
        courseUnlockData: null,
        isPollingActive: false,
      };
    },
    setIsPollingActive: (state, action) => {
      state.isPollingActive = action.payload;
    },
    cancelPolling: (state) => {
      state.longPollingLoading = false;
      state.isPollingActive = false;
      state.longPollingStatus = null;
      state.longPollingError = "Polling cancelled by user";
    },
  },
  extraReducers: (builder) => {
    builder
      // getUserOlduuid cases
      .addCase(getUserOlduuid.pending, (state) => {
        state.getUserOlduuidLoading = true;
        state.getUserOlduuidError = null;
      })
      .addCase(getUserOlduuid.fulfilled, (state, action) => {
        state.getUserOlduuidLoading = false;
        state.oldUuid = action.payload?.uid;
        state.getUserOlduuidError = null;
      })
      .addCase(getUserOlduuid.rejected, (state, action) => {
        state.getUserOlduuidLoading = false;
        state.getUserOlduuidError = action.payload;
      })
      
      // longPollCourseUnlocked cases
      .addCase(longPollCourseUnlocked.pending, (state) => {
        state.longPollingLoading = true;
        state.longPollingError = null;
        state.isPollingActive = true;
      })
      .addCase(longPollCourseUnlocked.fulfilled, (state, action) => {
        state.longPollingLoading = false;
        state.isPollingActive = false;
        
        if (action.payload?.success) {
          state.longPollingStatus = { is_course_unlocked: true };
          state.courseUnlockData = action.payload.data;
        } else {
          state.longPollingError = action.payload?.message || "Polling failed";
        }
      })
      .addCase(longPollCourseUnlocked.rejected, (state, action) => {
        state.longPollingLoading = false;
        state.longPollingError = action.payload;
        state.isPollingActive = false;
      });
  },
});




export const { setLongPollingStatus, resetPostPurchaseState, setIsPollingActive, cancelPolling } = postPurchaseSlice.actions;

// Selectors
export const selectOldUuid = (state) => state.postPurchase.oldUuid;
export const selectGetUserOlduuidLoading = (state) => state.postPurchase.getUserOlduuidLoading;
export const selectGetUserOlduuidError = (state) => state.postPurchase.getUserOlduuidError;

export const selectLongPollingStatus = (state) => state.postPurchase.longPollingStatus;
export const selectLongPollingLoading = (state) => state.postPurchase.longPollingLoading;
export const selectLongPollingError = (state) => state.postPurchase.longPollingError;

export const selectCourseUnlockData = (state) => state.postPurchase.courseUnlockData;
export const selectIsPollingActive = (state) => state.postPurchase.isPollingActive;

export default postPurchaseSlice.reducer;
